/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C5c §7] WHAT AN ORDER SEARCH READS, IN ONE SENTENCE — AND THE TERM A CLIENT SENDS.
 *
 *  The server has ONE predicate, `arrangementSearchPredicate`, behind the Manage Arrangements list,
 *  the Assign PA inbox and Duty Mitra's operations list. The three screens described it three ways —
 *  "Title or order number" (false: the order number was not read), "reference, title and places"
 *  and "Reference, title or place" — so one predicate now has one sentence, here, and Duty Mitra's
 *  catalogue holds the same words (`arrangementSearch.test.ts` reads it as data: the PWA sends every
 *  string through `t()`, so it cannot import this one).
 *
 *  ⚠️ §7b — BOTH NUMBERS ARE NAMED. The paper PA number first, because it is what every row leads
 *  with; the minted reference beside it, because typing either finds the order.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_SEARCH_READS = 'PA number, reference, title or place';

/**
 * ⚠️ THE SEARCH FIELD'S WIDTH, SIZED TO THAT SENTENCE — a placeholder clipped mid-word says less than a
 * shorter one. MEASURED from Inter's own advance widths at the 14px body size: the sentence is 235px.
 * An `Input.Search` spends 80px of its field on everything but text — the 38px search button
 * (`controlHeight`), 12px of padding and a 1px border either side, and the 16px the hidden clear icon
 * keeps reserved under `allowClear`. The old `w-56` (224px) left 144px, so the box read
 * "PA number, reference" and stopped; `w-84` (336px) leaves 256px.
 *
 * ⚠️ A LITERAL CLASS, NEVER BUILT — Tailwind reads source TEXT, so an interpolated width generates
 * nothing. Lengthen the sentence and re-measure; `arrangementSearch.test.ts` pins its length for that.
 */
export const ARRANGEMENT_SEARCH_FIELD_CLASS = 'w-84';

/**
 * ⚠️ THE TERM TO SEND — TRIMMED, AND `undefined` WHEN NOTHING IS LEFT.
 *
 * Every search schema is `.trim().min(1)`, so a term of spaces is a 422 on the server — a failed page
 * where the operator meant "no search". `useServerTable` keeps the box's raw value in the URL (so the
 * box shows what was typed); what reaches the server goes through this.
 */
export const searchTermOf = (raw: string): string | undefined => {
  const term = raw.trim();
  return term === '' ? undefined : term;
};
